/* eslint-disable strict, no-useless-escape */
/* osci-logo-v11-cycle.js
   - Vector-true silhouette sampling via SVGGeometryElement.getPointAtLength
   - Supports svgList[] and cycling to next logo after each "signal loss" (re-lock)
   - Keeps previous v10 API + adds:
       setSvgMarkup(markup), setSvgList(list, index), setSvgIndex(i), nextSvg(), prevSvg()
       cycleOnRelock (bool), svgIndex (int)
*/
(function(){
'use strict';

const DEFAULT_SVG_MARKUP = "<?xml version=\"1.0\" encoding=\"UTF-8\"?>\n<svg id=\"Layer_1\" data-name=\"Layer 1\" xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 44.98 48\">\n  <defs>\n    <style>\n      .cls-1 {\n        fill: red;\n      }\n    </style>\n  </defs>\n  <path class=\"cls-1\" d=\"m38.61,12.46v3.82h-5.94v-3.82h5.94Zm-5.09.85v2.12h4.24v-2.12h-4.24Z\"/>\n  <polygon class=\"cls-1\" points=\"37.77 17.76 31.19 17.76 31.19 13.31 20.79 13.31 20.79 16.28 19.94 16.28 19.94 12.46 32.04 12.46 32.04 16.91 38.61 16.91 37.77 17.76\"/>\n  <polygon class=\"cls-1\" points=\"30.55 17.76 30.55 13.94 29.7 13.94 29.7 16.28 25.46 13.94 24.61 13.94 24.61 17.76 25.46 17.76 25.46 15 30.55 17.76\"/>\n  <path class=\"cls-1\" d=\"m18.03,13.94h5.94v3.82h-5.94v-3.82Zm.85,2.97h4.24v-2.12h-4.24v2.12Z\"/>\n  <rect class=\"cls-1\" x=\"13.15\" y=\"19.67\" width=\"3.39\" height=\"26.95\"/>\n  <polygon class=\"cls-1\" points=\"12.52 13.33 12.52 30.49 17.19 30.49 17.19 19.05 13.15 19.05 13.15 18.2 18.03 18.2 18.03 31.34 8.7 31.34 9.55 27.94 9.55 15.21 6.58 12.48 19.31 12.48 19.31 13.33 12.52 13.33\"/>\n  <polygon class=\"cls-1\" points=\"26.95 11.82 28.36 10.12 44.98 10.12 44.98 11.82 26.95 11.82\"/>\n  <rect class=\"cls-1\" x=\"32.67\" y=\"4.18\" width=\"5.94\" height=\"7.64\"/>\n  <polygon class=\"cls-1\" points=\"26.31 4.18 19.94 11.82 26.31 11.82 32.67 4.18 26.31 4.18\"/>\n  <polygon class=\"cls-1\" points=\"18.88 4.18 25.67 4.18 19.31 11.82 12.52 11.82 18.88 4.18\"/>\n  <rect class=\"cls-1\" x=\"6.58\" y=\"4.18\" width=\"5.94\" height=\"7.64\"/>\n  <rect class=\"cls-1\" y=\"4.18\" width=\"5.94\" height=\"7.64\"/>\n  <rect class=\"cls-1\" y=\"1.85\" width=\"5.94\" height=\"1.7\"/>\n</svg>";

function clamp(v,a,b){return Math.max(a,Math.min(b,v));}
function lerp(a,b,t){return a+(b-a)*t;}
function mod(a,m){return ((a%m)+m)%m;}
function hexToRgb(hex){
  const h=(hex||'#ff2b2b').replace('#','').trim();
  const r=parseInt(h.length===3?h[0]+h[0]:h.slice(0,2),16);
  const g=parseInt(h.length===3?h[1]+h[1]:h.slice(2,4),16);
  const b=parseInt(h.length===3?h[2]+h[2]:h.slice(4,6),16);
  return {r:r|0,g:g|0,b:b|0};
}
function rgba(rgb,a){return 'rgba('+rgb.r+','+rgb.g+','+rgb.b+','+a+')';}

function mulberry32(seed){
  let t = seed>>>0;
  return function(){
    t += 0x6D2B79F5;
    let x = Math.imul(t ^ (t>>>15), 1|t);
    x ^= x + Math.imul(x ^ (x>>>7), 61|x);
    return ((x ^ (x>>>14))>>>0) / 4294967296;
  };
}
function makeNoise2D(seed){
  const rnd=mulberry32(seed>>>0);
  const table=new Float32Array(2048);
  for(let i=0;i<table.length;i++) table[i]=rnd()*2-1;
  const n=table.length;
  const fade=t=>t*t*(3-2*t);
  const at=i=>table[mod(i,n)];
  return function(x,y){
    const xi=x|0, yi=y|0;
    const tx=x-xi, ty=y-yi;
    const a=at(xi + yi*57);
    const b=at(xi+1 + yi*57);
    const c=at(xi + (yi+1)*57);
    const d=at(xi+1 + (yi+1)*57);
    const ux=fade(tx), uy=fade(ty);
    const ab=a+(b-a)*ux;
    const cd=c+(d-c)*ux;
    return ab+(cd-ab)*uy;
  };
}

function parseViewBox(svg){
  const m=/viewBox\s*=\s*["']\s*([-\d\.eE]+)[\s,]+([-\d\.eE]+)[\s,]+([-\d\.eE]+)[\s,]+([-\d\.eE]+)\s*["']/i.exec(svg||'');
  if(!m) return {x:0,y:0,w:100,h:100};
  const x=parseFloat(m[1]),y=parseFloat(m[2]),w=parseFloat(m[3]),h=parseFloat(m[4]);
  if(![x,y,w,h].every(Number.isFinite) || w===0 || h===0) return {x:0,y:0,w:100,h:100};
  return {x,y,w,h};
}

class OsciLogoAnimator{
  constructor(opts){
    if(!opts||!opts.canvas) throw new Error('OsciLogoAnimator: opts.canvas is required');
    this.canvas=opts.canvas;

    this.transparent = !!opts.transparent;
    this.trailFade = ('trailFade' in opts) ? clamp(+opts.trailFade, 0, 0.6) : 0.12;

    try {
      this.ctx=this.canvas.getContext('2d',{alpha: this.transparent ? true : false, desynchronized:true});
    }
    catch (e) { this.ctx=this.canvas.getContext('2d'); }
    if(!this.ctx) throw new Error('2D canvas not supported');

    const list = Array.isArray(opts.svgList) && opts.svgList.length ? opts.svgList.slice() : null;
    this.svgList = list || [opts.svgMarkup||DEFAULT_SVG_MARKUP];
    this.svgIndex = ('svgIndex' in opts) ? (opts.svgIndex|0) : 0;
    this.svgIndex = clamp(this.svgIndex, 0, this.svgList.length-1);
    this.cycleOnRelock = ('cycleOnRelock' in opts) ? !!opts.cycleOnRelock : false;

    this.svgMarkup = this.svgList[this.svgIndex];

    this._vb=parseViewBox(this.svgMarkup);
    this._ratio=this._vb.h/this._vb.w;

    this.beamColor = opts.beamColor || opts.color || '#ff2b2b';
    this.baseOutlineColor = opts.baseOutlineColor || this.beamColor;
    this.bg = opts.background || '#000';

    this.color = this.beamColor; // backward-compat
    this._rgb = hexToRgb(this.beamColor);
    this._baseRgb = hexToRgb(this.baseOutlineColor);
    this._bgRgb = hexToRgb(this.bg);

    const isMobile=/Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent||'');
    this.quality=('quality'in opts)?clamp(opts.quality,0.5,1.0):(isMobile?0.7:0.85);
    this.renderScale=('renderScale'in opts)?clamp(opts.renderScale,0.25,1.0):0.55;
    this.maxFps=('maxFps'in opts)?clamp(opts.maxFps|0,20,60):50;
    this.adaptive=('adaptive'in opts)?!!opts.adaptive:true;

    this.direction=('direction'in opts)?(opts.direction>=0?1:-1):1;
    this.speed=('speed'in opts)?opts.speed:1.6;
    this.speedBoost=('speedBoost'in opts)?opts.speedBoost:1.0;
    this.intensity=('intensity'in opts)?opts.intensity:1.15;
    this.beams=('beams'in opts)?clamp(opts.beams|0,1,3):2;
    this.highlightWidthPx=('highlightWidthPx'in opts)?opts.highlightWidthPx:34;

    this.traceStepPx=('traceStepPx'in opts)?opts.traceStepPx:1.15;
    this.strokePx=('strokePx'in opts)?opts.strokePx:1.15;
    this.glowPx=('glowPx'in opts)?opts.glowPx:10;

    // Base outline thickness (independent from main strokePx)
    this.baseOutlineStrokePx = ('baseOutlineStrokePx' in opts) ? Math.max(0.2, +opts.baseOutlineStrokePx) : this.strokePx;
    this.miterLimit=('miterLimit'in opts)?opts.miterLimit:6;

    this.frameJitter=('frameJitter'in opts)?clamp(opts.frameJitter,0,18):0.7;
    this.stableJitterPx=('stableJitterPx'in opts)?opts.stableJitterPx:0.25;
    this.lostJitterPx=('lostJitterPx'in opts)?opts.lostJitterPx:6.0;
    this.dropout=('dropout'in opts)?clamp(opts.dropout,0,0.95):0.05;
    this.glitch=('glitch'in opts)?!!opts.glitch:true;
    this.glitchChance=('glitchChance'in opts)?clamp(opts.glitchChance,0,0.6):0.06;

    
    this.rgbAberration = ('rgbAberration' in opts) ? clamp(+opts.rgbAberration, 0, 24) : 6;

    // Default OFF: no stripes unless user explicitly enables.
    this.scanlinesEnabled = ('scan' in opts) ? !!opts.scan : (('scanlines' in opts) ? !!opts.scanlines : false);
    this.vignetteSize = ('vig' in opts) ? clamp(+opts.vig || 0, 0, 1) : (('vignette' in opts) ? clamp(+opts.vignette || 0, 0, 1) : 0);
    this._vgKey = '';


    this._gT=999;
    this._gDur=0;
    this._gActive=0;
    this._gBands=[];
    this._gdx=0; this._gdy=0;
this.signalEnabled=('signalEnabled'in opts)?!!opts.signalEnabled:true;
    this.signalNoise=('signalNoise'in opts)?clamp(opts.signalNoise,0,2.0):1.0;
    this.signalHoldMin=('signalHoldMin'in opts)?opts.signalHoldMin:1.7;
    this.signalHoldMax=('signalHoldMax'in opts)?opts.signalHoldMax:4.4;
    this.signalDropMin=('signalDropMin'in opts)?opts.signalDropMin:0.22;
    this.signalDropMax=('signalDropMax'in opts)?opts.signalDropMax:1.05;

    this.baseOutlineAlpha=('baseOutlineAlpha'in opts)?opts.baseOutlineAlpha:0.22;

    this._noise=makeNoise2D((Math.random()*1e9)|0);
    this._t=0;
    this._paused=false;

    this._sig=1.0;
    this._sigTarget=1.0;
    this._sigTimer=this._rand(this.signalHoldMin,this.signalHoldMax);
    this._wasLost=false;
    this._cycledThisRelock=false;
    // Organic morph (noise -> reveal) for cycling between SVGs
    this.morphRevealDur = ('morphRevealDur' in opts) ? clamp(opts.morphRevealDur, 0.05, 5.0) : 1.15;
    this.morphDeepSig = ('morphDeepSig' in opts) ? clamp(opts.morphDeepSig, 0.02, 0.60) : 0.12;
    this._relockActive = false;
    this._relockT = 999;
    this._relockReveal = 1;
    this._morphPending = false;
    this._morphThisCycle = false;
    this._morphHide = false;
    this._morphChaos = 0;
    this._morphSwapped = false;

    // True oscilloscope-style signal loss: ONLY the rays/trace morph.
    // No fog/static overlays, no VHS bars, no post-process noise.
    this._chaosPolylines = [];
    this._totalLenChaos = 1;
    this._shapeMix = 0; // 0 = silhouette, 1 = chaos


    this._mountSvgSampler(this.svgMarkup);
    this._prepareFxTiles();

    this._resize=this._resize.bind(this);
    window.addEventListener('resize',this._resize,{passive:true});
    this._resize(true);
  }

  togglePause(){this._paused=!this._paused;}
  toggleDirection(){this.direction*=-1;}
  setDirection(d){this.direction=(d>=0)?1:-1;}
  setBeams(n){this.beams=clamp(n|0,1,3);}
  setSpeed(v){this.speed=v;}
  setIntensity(v){this.intensity=v;}
  setRenderScale(v){this.renderScale=clamp(v,0.25,1.0); this._resize(true);}
  setMaxFps(v){this.maxFps=clamp(v|0,20,60);}
  setQuality(v){this.quality=clamp(v,0.5,1.0); this._resize(true);}
  setAdaptive(v){this.adaptive=!!v;}
  setScanlinesEnabled(v){this.scanlinesEnabled=!!v;}
  setVignetteSize(v){
    const nv=clamp(+v||0,0,1);
    if(nv===this.vignetteSize) return;
    this.vignetteSize=nv;
    this._updateVignetteTexture();
  }
  setSignalEnabled(v){this.signalEnabled=!!v;}
  setSignal(p){
    if(!p) return;
    if('signalNoise'in p) this.signalNoise=clamp(p.signalNoise,0,2.0);
    if('signalHoldMin'in p) this.signalHoldMin=Math.max(0.05, +p.signalHoldMin);
    if('signalHoldMax'in p) this.signalHoldMax=Math.max(this.signalHoldMin, +p.signalHoldMax);
    if('signalDropMin'in p) this.signalDropMin=Math.max(0.02, +p.signalDropMin);
    if('signalDropMax'in p) this.signalDropMax=Math.max(this.signalDropMin, +p.signalDropMax);
  }
  setSilhouette(p){
    if(!p) return;
    let rebuild=false;
    if('traceStepPx'in p){this.traceStepPx=Math.max(0.35, +p.traceStepPx); rebuild=true;}
    if('strokePx'in p) this.strokePx=Math.max(0.2, +p.strokePx);
    if('baseOutlineStrokePx'in p) this.baseOutlineStrokePx=Math.max(0.2, +p.baseOutlineStrokePx);
    if('glowPx'in p) this.glowPx=Math.max(0, +p.glowPx);
    if('miterLimit'in p) this.miterLimit=Math.max(1, +p.miterLimit);
    if(rebuild) this._rebuildSamples();
  }
  setBeam(p){ if(p && 'highlightWidthPx'in p) this.highlightWidthPx=Math.max(6, +p.highlightWidthPx); }

  setColors(p){
    if(!p) return;
    if('background' in p){
      this.bg = p.background || this.bg;
      this._bgRgb = hexToRgb(this.bg);
      if(!this.transparent){
        this.ctx.fillStyle=this.bg;
        this.ctx.fillRect(0,0,this._w,this._h);
      } else {
        this.ctx.clearRect(0,0,this._w,this._h);
      }
    }
    if('beamColor' in p){
      this.beamColor = p.beamColor || this.beamColor;
      this.color = this.beamColor; // backward-compat
      this._rgb = hexToRgb(this.beamColor);
      this._rebuildDustStamp();
    }
    if('baseOutlineColor' in p){
      this.baseOutlineColor = p.baseOutlineColor || this.baseOutlineColor;
      this._baseRgb = hexToRgb(this.baseOutlineColor);
    }
  }

  setSvgList(list, index=0){
    if(!Array.isArray(list) || !list.length) return;
    this.svgList = list.slice();
    this.setSvgIndex(index|0);
  }
  setSvgIndex(i){
    if(!this.svgList || !this.svgList.length) return;
    const ni = clamp(i|0, 0, this.svgList.length-1);
    this.svgIndex = ni;
    this.setSvgMarkup(this.svgList[this.svgIndex], false);
  }
  nextSvg(){
    if(!this.svgList || this.svgList.length<2) return;
    this.setSvgIndex(mod(this.svgIndex+1, this.svgList.length));
  }
  prevSvg(){
    if(!this.svgList || this.svgList.length<2) return;
    this.setSvgIndex(mod(this.svgIndex-1, this.svgList.length));
  }
  setSvgMarkup(markup, resetSignal=true){
    if(!markup || typeof markup!=='string') return;
    if(markup === this.svgMarkup) return;

    this.svgMarkup = markup;
    this._vb = parseViewBox(this.svgMarkup);
    this._ratio = this._vb.h/this._vb.w;

    if(this._samplerRoot && this._samplerRoot.parentNode) {
      this._samplerRoot.parentNode.removeChild(this._samplerRoot);
    }
    this._mountSvgSampler(this.svgMarkup);
    this._resize(true);

    if(resetSignal) {
      this._sig = 0.15;
      this._sigTarget = 1.0;
      this._sigTimer = this._rand(this.signalHoldMin,this.signalHoldMax);
      this._wasLost = true;
      this._cycledThisRelock = true;
    }
  }

  start(){
    this._last=performance.now();
    this._lastRender=0;
    const loop=(now)=>{
      const minStep=1000/this.maxFps;
      if(now-this._lastRender<minStep){ this._raf=requestAnimationFrame(loop); return; }
      const dt=Math.min(0.05,(now-this._last)/1000);
      const ms=now-this._last;
      this._last=now;
      this._lastRender=now;

      if(this.adaptive) this._adapt(ms);
      if(!this._paused) this._frame(dt);

      this._raf=requestAnimationFrame(loop);
    };
    this._raf=requestAnimationFrame(loop);
  }

  destroy(){
    cancelAnimationFrame(this._raf);
    window.removeEventListener('resize',this._resize);
    if(this._samplerRoot && this._samplerRoot.parentNode) this._samplerRoot.parentNode.removeChild(this._samplerRoot);
  }

  _rand(a,b){return a+Math.random()*(b-a);}

  _adapt(ms){
    this._emaMs=(this._emaMs??16.7)*0.9+ms*0.1;
    if(this._emaMs>24){this._slow=(this._slow??0)+1;}
    else this._slow=Math.max(0,(this._slow??0)-1);
    if((this._slow??0)>14 && this.quality>0.6){
      this.quality=clamp(this.quality*0.92,0.5,1.0);
      this._slow=0;
      this._resize(true);
    }
  }

  _prepareFxTiles(){
    const s=document.createElement('canvas');
    s.width=4;s.height=4;
    const sctx=s.getContext('2d');
    sctx.fillStyle='rgba(0,0,0,1)';sctx.fillRect(0,0,4,4);
    sctx.fillStyle='rgba(0,0,0,0.70)';sctx.fillRect(0,0,4,1);
    sctx.fillStyle='rgba(255,255,255,0.06)';sctx.fillRect(0,2,4,1);
    this._scanTile=s;

    this._vignetteFull=document.createElement('canvas');
    this._vignetteCtx=this._vignetteFull.getContext('2d');
    this._vgKey='';

    // --- Organic morph: chaos/static buffers ---
    this._staticCanvas = document.createElement('canvas');
    this._staticCtx = this._staticCanvas.getContext('2d', { willReadFrequently:true });
    this._staticData = null;
    this._staticLastT = -999;

    this._dustStamp = document.createElement('canvas');
    this._rebuildDustStamp();

    // Analog warp/feedback buffers (for organic signal distortion; avoids VHS bars).
    this._warpCanvas = document.createElement('canvas');
    this._warpCtx = this._warpCanvas.getContext('2d', { willReadFrequently:false });
    this._fbCanvas = document.createElement('canvas');
    this._fbCtx = this._fbCanvas.getContext('2d', { alpha:true });
  }


  _rebuildDustStamp(){
    // Soft blob used for "chaotic dust clouds" during morph.
    const c = this._dustStamp || (this._dustStamp = document.createElement('canvas'));
    const S = 64;
    if(c.width !== S || c.height !== S){ c.width = S; c.height = S; }
    const dctx = c.getContext('2d');
    dctx.setTransform(1,0,0,1,0,0);
    dctx.clearRect(0,0,S,S);

    const col = this._rgb || [255,43,43];
    const r = col[0]|0, g = col[1]|0, b = col[2]|0;

    const grad = dctx.createRadialGradient(S/2,S/2, 0, S/2,S/2, S/2);
    grad.addColorStop(0.00, `rgba(${r},${g},${b},0.85)`);
    grad.addColorStop(0.28, `rgba(${r},${g},${b},0.26)`);
    grad.addColorStop(1.00, `rgba(${r},${g},${b},0.0)`);
    dctx.fillStyle = grad;
    dctx.fillRect(0,0,S,S);
  }

  _ensureChaosBuffers(){
    const w = this._w||0, h = this._h||0;
    if(w<2 || h<2) return;

    // Keep it small for speed; it gets scaled up.
    const sw = clamp(Math.floor(w/3.2), 90, 260);
    const sh = clamp(Math.floor(h/3.2), 90, 260);

    const c = this._staticCanvas || (this._staticCanvas = document.createElement('canvas'));
    if(c.width !== sw || c.height !== sh){
      c.width = sw; c.height = sh;
      this._staticCtx = c.getContext('2d', { willReadFrequently:true });
      this._staticData = this._staticCtx.createImageData(sw, sh);
      this._staticLastT = -999;
    }
  }


_updateStaticTexture(force=false){
  // Low-res analog "plasma" noise texture (cloudy, not scanline/VHS).
  const c = this._staticCanvas, sctx = this._staticCtx, img = this._staticData;
  if(!c || !sctx || !img) return;

  const t = this._t || 0;
  if(!force && (t - (this._staticLastT||0)) < (1/22)) return; // ~22 Hz
  this._staticLastT = t;

  const sw = c.width, sh = c.height;
  const data = img.data;

  const col = this._rgb || [255,43,43];
  const cr = (col[0]/255), cg = (col[1]/255), cb = (col[2]/255);

  const ox = t*0.18, oy = -t*0.15;
  const baseF = 3.2;

  let i = 0;
  for(let y=0;y<sh;y++){
    const ny = (y/sh - 0.5);
    for(let x=0;x<sw;x++){
      const nx = (x/sw - 0.5);

      // 3-octave fbm (cheap).
      let f=0, amp=1, freq=1;
      for(let o=0;o<3;o++){
        const n = this._noise((nx*baseF*freq)+ox*freq + o*17.1, (ny*baseF*freq)+oy*freq + o*9.7);
        f += amp*(0.5+0.5*n);
        amp *= 0.5;
        freq *= 2.0;
      }
      f /= (1 + 0.5 + 0.25);

      // shape + subtle grain
      const shaped = Math.pow(f, 1.75);
      const grain = 0.78 + 0.22*Math.random();

      let v = shaped * 255 * grain;

      // occasional hot specks
      if(Math.random() < 0.004) v = 255;

      const vv = v|0;
      data[i  ] = (vv*cr)|0;
      data[i+1] = (vv*cg)|0;
      data[i+2] = (vv*cb)|0;
      data[i+3] = 255;
      i += 4;
    }
  }

  sctx.putImageData(img, 0, 0);
}

  _computeChaos(sig, reveal){
    // 0 = clean signal, 1 = full chaos/static.
    const s = clamp(sig, 0, 1);
    const r = clamp(reveal==null ? 1 : reveal, 0, 1);

    // Start chaos fairly early so the transition reads clearly.
    let c = clamp((0.92 - s) / 0.92, 0, 1);

    // During reveal, keep chaos on top until the shape is "born".
    c = Math.max(c, clamp(1 - r, 0, 1));

    // After we swap SVG in the deepest static, keep it fully hidden until relock starts.
    if(this._morphHide) c = Math.max(c, 0.88);

    // Weight by user signalNoise a bit (but never goes to zero).
    c = clamp(c * (0.65 + 0.35*clamp(this.signalNoise||0, 0, 2)), 0, 1);
    return c;
  }

  _computeShapeMix(sig, reveal){
    // 0 = silhouette, 1 = chaos.
    // Loss part: starts morphing once the lock weakens.
    const s = clamp(sig, 0, 1);
    const r = clamp(reveal==null ? 1 : reveal, 0, 1);
    const loss = 1 - s;

    // Start morphing a bit before full loss.
    let m = clamp((loss - 0.10) / 0.60, 0, 1);
    // smoothstep
    m = m*m*(3 - 2*m);

    // During relock, we want: chaos -> silhouette. So keep chaos high while reveal is low.
    m = Math.max(m, clamp(1 - r, 0, 1));

    // While we hide the swapped SVG during deepest loss, force full chaos.
    if(this._morphHide) m = 1;

    // Small weighting by user knob.
    const sn = clamp(this.signalNoise||0, 0, 2);
    m = clamp(m * (0.80 + 0.20*sn), 0, 1);
    return m;
  }

  _updateChaosPolylinesDynamic(mix){
    // Build a smooth, oscilloscope-like chaotic trace in viewBox space.
    // This is what you see when the signal is lost.
    const dst = this._chaosPolylines;
    if(!dst || !dst.length) return;

    const vb = this._vb;
    const cx = vb.x + vb.w*0.5;
    const cy = vb.y + vb.h*0.5;
    const t = this._t || 0;
    const TAU = Math.PI*2;

    const sn = clamp(this.signalNoise||0, 0, 2);
    const ampBase = (0.42 + 0.58*mix) * (0.70 + 0.30*sn);
    const ax = vb.w * 0.46 * ampBase;
    const ay = vb.h * 0.46 * ampBase;

    let globalS = 0;
    for(let li=0; li<dst.length; li++){
      const line = dst[li];
      const n = line.length;
      if(n < 2) continue;

      // Deterministic per-line parameters.
      const nf1 = 0.5 + 0.5*this._noise(li*7.17, 1.3);
      const nf2 = 0.5 + 0.5*this._noise(li*7.17, 9.1);
      const np1 = 0.5 + 0.5*this._noise(li*3.77, 2.2);
      const np2 = 0.5 + 0.5*this._noise(li*3.77, 8.8);
      const nw  = 0.5 + 0.5*this._noise(li*1.93, 5.5);

      const f1 = 1.4 + 2.7*nf1;
      const f2 = 1.3 + 2.8*nf2;
      const ph1 = TAU*np1;
      const ph2 = TAU*np2;
      const wob = 0.65 + 1.85*nw;

      const tt = t * (0.85 + 1.15*mix) * wob;

      let prevX = 0, prevY = 0;
      let localS = 0;

      for(let i=0;i<n;i++){
        const u = (n===1)?0:(i/(n-1));
        const th = TAU*u;

        // Phase noise to break up clean Lissajous symmetry (still smooth in time).
        const phJ = (0.18*mix) * TAU * (0.5 + 0.5*this._noise(li*0.7 + u*2.1, tt*0.15));
        const hf  = this._noise(u*12.0 + tt*0.35, li*3.3) * (0.8 + 0.6*mix);

        // Lissajous-ish core + second harmonic for density.
        let x = cx + ax * (
          Math.sin(th*f1 + tt*1.10 + ph1 + phJ) + 0.35*Math.sin(th*(f1*0.52+1.05) - tt*1.75 + ph2 - phJ)
        );
        let y = cy + ay * (
          Math.sin(th*f2 - tt*1.25 + ph2 - phJ) + 0.35*Math.sin(th*(f2*0.50+1.00) + tt*1.60 + ph1 + phJ)
        );

        // Extra turbulent harmonic (only in chaos) to make the trace feel like a drifting, unstable lock.
        if(mix>0.01){
          const amp = 0.18*mix;
          x += ax*amp * Math.sin(th*(f1*2.35 + 1.15) + tt*2.05 + ph2 + hf);
          y += ay*amp * Math.sin(th*(f2*2.15 + 1.05) - tt*2.15 + ph1 - hf);
        }

        // Curl-like drift to make it feel like unstable sync, not random bars.
        const dx = this._noise(u*4.0 + tt*0.22, li*12.7 + 3.1);
        const dy = this._noise(li*11.3 + 7.9, u*4.1 - tt*0.20);
        const drift = (0.035 + 0.055*mix) * (0.75 + 0.25*sn);
        x += dx * vb.w * drift;
        y += dy * vb.h * drift;

        const p = line[i];
        p.x = x; p.y = y;

        if(i === 0){
          localS = 0;
          p.s = globalS;
        } else {
          localS += Math.hypot(x - prevX, y - prevY);
          p.s = globalS + localS;
        }
        prevX = x; prevY = y;
      }

      globalS += localS;
    }

    this._totalLenChaos = Math.max(1e-6, globalS);
  }


_ensureWarpBuffers(){
  const w = this._w||0, h = this._h||0;
  if(w<2 || h<2) return;

  if(!this._warpCanvas){
    this._warpCanvas = document.createElement('canvas');
    this._warpCtx = this._warpCanvas.getContext('2d', { willReadFrequently:false });
  }
  if(this._warpCanvas.width !== w || this._warpCanvas.height !== h){
    this._warpCanvas.width = w; this._warpCanvas.height = h;
    this._warpCtx = this._warpCanvas.getContext('2d', { willReadFrequently:false });
  }

  if(!this._fbCanvas){
    this._fbCanvas = document.createElement('canvas');
    this._fbCtx = this._fbCanvas.getContext('2d', { alpha:true });
  }
  if(this._fbCanvas.width !== w || this._fbCanvas.height !== h){
    this._fbCanvas.width = w; this._fbCanvas.height = h;
    this._fbCtx = this._fbCanvas.getContext('2d', { alpha:true });
  }
}

_applyAnalogWarp(chaos){
  // Heat-haze / phase wobble warp (patch-based), no VHS bars.
  if(!chaos || chaos<=0.06) return;
  this._ensureWarpBuffers();
  const src = this._warpCanvas, sctx = this._warpCtx;
  if(!src || !sctx) return;

  const ctx = this.ctx;
  const w = this._w, h = this._h;
  const px = this._px || 1;
  const t = this._t || 0;

  // snapshot
  sctx.setTransform(1,0,0,1,0,0);
  sctx.globalCompositeOperation = 'source-over';
  sctx.globalAlpha = 1;
  sctx.drawImage(this.canvas, 0, 0);

  const strength = (3 + 24*chaos) * px;
  const cols = 14, rows = 14;
  const pw = Math.ceil(w/cols);
  const ph = Math.ceil(h/rows);

  ctx.save();
  ctx.globalCompositeOperation = 'screen';
  ctx.globalAlpha = clamp(0.10 + 0.55*chaos, 0.10, 0.65);
  ctx.imageSmoothingEnabled = true;

  for(let ry=0; ry<rows; ry++){
    for(let rx=0; rx<cols; rx++){
      const sx = rx*pw, sy = ry*ph;
      const sw = Math.min(pw, w-sx), sh = Math.min(ph, h-sy);
      if(sw<=0 || sh<=0) continue;
      const cx = sx + sw*0.5, cy = sy + sh*0.5;

      const n1 = this._noise(cx*0.008 + t*1.15, cy*0.008 + 12.3);
      const n2 = this._noise(cx*0.008 + 37.7, cy*0.008 + t*1.05);

      const dx = n1 * strength;
      const dy = n2 * strength * 0.75;

      ctx.drawImage(src, sx, sy, sw, sh, sx+dx, sy+dy, sw, sh);
    }
  }
  ctx.restore();
}

_applyFeedback(chaos){
  // Faint ghosting/feedback that reads "analog" (subtle), no scanline aesthetic.
  if(!chaos || chaos<=0.04) return;
  this._ensureWarpBuffers();
  const fb = this._fbCanvas, fctx = this._fbCtx;
  if(!fb || !fctx) return;

  const w = this._w, h = this._h;
  const ctx = this.ctx;
  const px = this._px || 1;
  const t = this._t || 0;

  // decay previous
  fctx.save();
  fctx.globalCompositeOperation = 'source-over';
  fctx.fillStyle = 'rgba(0,0,0,' + clamp(0.06 + 0.16*chaos, 0.06, 0.22) + ')';
  fctx.fillRect(0,0,w,h);

  // inject current frame with tiny drift
  const dx = this._noise(t*0.75, 51.3) * (2 + 10*chaos) * px;
  const dy = this._noise(17.7, t*0.72) * (2 + 8*chaos) * px;

  fctx.globalCompositeOperation = 'screen';
  fctx.globalAlpha = clamp(0.22 + 0.38*chaos, 0.22, 0.60);
  fctx.drawImage(this.canvas, dx, dy);
  fctx.restore();

  // composite back
  ctx.save();
  ctx.globalCompositeOperation = 'screen';
  ctx.globalAlpha = clamp(0.06 + 0.26*chaos, 0.06, 0.34);
  ctx.drawImage(fb, 0, 0);
  ctx.restore();
}

_drawChaosOverlay(chaos){
  if(!chaos || chaos<=0) return;

  const ctx = this.ctx;
  const w = this._w, h = this._h;
  const px = this._px || 1;
  const t  = this._t || 0;

  this._ensureChaosBuffers();
  this._updateStaticTexture(false);

  const a = clamp(0.10 + chaos*0.78, 0, 0.92);

  ctx.save();
  ctx.globalCompositeOperation = 'screen';

  // --- Cloudy "signal fog" ---
  if(this._staticCanvas){
    ctx.globalAlpha = a * 0.85;
    ctx.imageSmoothingEnabled = true;

    const driftX = this._noise(t*0.35, 91.1) * 24 * px;
    const driftY = this._noise(13.7, t*0.33) * 18 * px;
    ctx.drawImage(this._staticCanvas, driftX, driftY, w, h);

    // subtle sharper grain pass
    ctx.globalAlpha = a * 0.18;
    ctx.imageSmoothingEnabled = false;
    const driftX2 = this._noise(t*1.15, 11.2) * 12 * px;
    const driftY2 = this._noise(7.9, t*1.05) * 10 * px;
    ctx.drawImage(this._staticCanvas, driftX2, driftY2, w, h);
  }

  // --- Volumetric dust ---
  if(this._dustStamp){
    ctx.globalAlpha = a * 0.55;
    const n = (12 + chaos*90)|0;
    for(let i=0;i<n;i++){
      const nx = 0.5 + 0.5*this._noise(i*9.7 + 10.1, t*0.45 + 2.0);
      const ny = 0.5 + 0.5*this._noise(i*7.3 + 33.7, t*0.45 + 9.0);
      const x = nx*w;
      const y = ny*h;

      const ns = 0.5 + 0.5*this._noise(i*3.1 + 101.0, t*0.70 + 15.0);
      const size = (12 + (18 + 82*chaos) * ns) * px;

      ctx.drawImage(this._dustStamp, x - size*0.5, y - size*0.5, size, size);
    }
  }

  // --- Shape-linked haze (morph reads like "forming from chaos") ---
  const reveal = this._relockReveal || 1;
  const shapeHaze = (!this._morphHide) ? clamp((1 - reveal)*1.15 + chaos*0.55, 0, 1) : 0;

  if(shapeHaze>0.04 && this._polylines && this._polylines.length && this._dustStamp){
    const count = Math.min(800, (80 + 520*shapeHaze)|0);
    ctx.globalAlpha = a * (0.35 + 0.35*shapeHaze);

    const j = (8 + 70*chaos) * px;

    for(let k=0;k<count;k++){
      const line = this._polylines[(Math.random()*this._polylines.length)|0];
      if(!line || line.length<2) continue;
      const p = line[(Math.random()*line.length)|0];
      if(!p) continue;

      const pn = this._mapToCanvas(p);

      const n1 = this._noise(p.x*0.45 + t*1.2,  p.y*0.45 + 7.1);
      const n2 = this._noise(p.x*0.45 + 19.9, p.y*0.45 + t*1.15);

      const x = pn.x + n1*j;
      const y = pn.y + n2*j;

      const sN = 0.5 + 0.5*this._noise(p.x*0.9+3.7, p.y*0.9+t*0.8);
      const size = (6 + (18 + 55*shapeHaze) * sN) * px;

      ctx.drawImage(this._dustStamp, x - size*0.5, y - size*0.5, size, size);
    }
  }

  // --- tiny sparks (dots, not lines) ---
  if(chaos>0.18 && this._dustStamp){
    ctx.globalAlpha = a * 0.22;
    const m = (20 + chaos*160)|0;
    for(let i=0;i<m;i++){
      const x = Math.random()*w;
      const y = Math.random()*h;
      const s = (2 + Math.random()*10) * px;
      ctx.drawImage(this._dustStamp, x - s*0.5, y - s*0.5, s, s);
    }
  }

  ctx.restore();
}


  _mountSvgSampler(markup){
    const root=document.createElement('div');
    root.style.position='absolute';
    root.style.left='-99999px';
    root.style.top='-99999px';
    root.style.width='0';
    root.style.height='0';
    root.style.overflow='hidden';
    root.setAttribute('aria-hidden','true');

    root.innerHTML=markup;
    const svg=root.querySelector('svg');
    if(!svg) throw new Error('SVG markup has no <svg> root');

    svg.setAttribute('width', String(this._vb.w));
    svg.setAttribute('height', String(this._vb.h));
    svg.setAttribute('preserveAspectRatio','xMidYMid meet');

    svg.querySelectorAll('*').forEach(el=>{
      try {
        el.setAttribute('fill', el.getAttribute('fill') || '#fff');
        el.setAttribute('stroke', 'none');
      } catch(e){}
    });

    document.body.appendChild(root);

    this._samplerRoot=root;
    this._svgEl=svg;

    const sel='path,rect,circle,ellipse,line,polyline,polygon';
    this._geoms=Array.from(svg.querySelectorAll(sel)).filter(el=>typeof el.getTotalLength==='function');
    if(!this._geoms.length) throw new Error('No SVG geometry elements found to trace');

    this._geomLens=this._geoms.map(g=>Math.max(0, g.getTotalLength()));
    this._totalLenView=this._geomLens.reduce((a,b)=>a+b,0) || 1;

    this._rebuildSamples();
  }

  _ensureCssHeight(rect){
    if(rect.width>10 && rect.height<2){
      const h=Math.max(1, Math.round(rect.width*this._ratio));
      this.canvas.style.height = h + 'px';
      rect = this.canvas.getBoundingClientRect();
    }
    return rect;
  }

  _resize(force){
    let rect=this.canvas.getBoundingClientRect();
    rect=this._ensureCssHeight(rect);

    let cw=rect.width;
    let ch=rect.height;
    if(cw<2) cw=320;
    if(ch<2) ch=cw*this._ratio;

    const dpr=window.devicePixelRatio||1;
    const pxScale=dpr*this.quality*this.renderScale;

    const w=Math.max(2, Math.floor(cw*pxScale));
    const h=Math.max(2, Math.floor(ch*pxScale));

    if(!force && this.canvas.width===w && this.canvas.height===h) return;

    this.canvas.width=w;
    this.canvas.height=h;
    this._w=w; this._h=h;

    const pad=26*(w/cw);
    const fitW=Math.max(10, w-pad);
    const fitH=Math.max(10, h-pad);
    this._scale=Math.min(fitW/this._vb.w, fitH/this._vb.h);
    const drawW=this._vb.w*this._scale;
    const drawH=this._vb.h*this._scale;
    this._dx=(w-drawW)*0.5;
    this._dy=(h-drawH)*0.5;

    this._px = w/cw;

    this._rebuildSamples();

    this._ensureChaosBuffers();

    this._ensureWarpBuffers();
    this._updateVignetteTexture();

    if(!this.transparent){
      this.ctx.fillStyle=this.bg;
      this.ctx.fillRect(0,0,w,h);
    } else {
      this.ctx.clearRect(0,0,w,h);
    }
}

  _rebuildSamples(){
    if(!this._geoms || !this._geoms.length) return;
    const scale=this._scale || 1;
    const stepPx=this.traceStepPx;

    const all=[];
    let s=0;

    for(let gi=0;gi<this._geoms.length;gi++) {
      const g=this._geoms[gi];
      const len=Math.max(0, this._geomLens[gi] || g.getTotalLength());
      if(len<=0) continue;

      const pxLen=len*scale;
      let n=Math.ceil(pxLen/stepPx);
      n=clamp(n, 8, 1400);
      const pts=new Array(n);
      let prev=null;

      for(let i=0;i<n;i++) {
        const t = (n===1)?0:(i/(n-1));
        const l = t*len;
        const p = g.getPointAtLength(l);
        const x=p.x, y=p.y;

        if(prev) {
          const dx=x-prev.x, dy=y-prev.y;
          s += Math.hypot(dx,dy);
        }
        pts[i]={x,y,s};
        prev={x,y};
      }
      all.push(pts);
    }

    this._polylines=all;
    this._totalLenView = s>0 ? s : (this._totalLenView||1);

    // Allocate chaos buffers with the same topology (polyline count + point count)
    // so we can morph purely by moving the trace.
    this._ensureChaosPolylines();
  }

  _ensureChaosPolylines(){
    const src = this._polylines;
    if(!src || !src.length){ this._chaosPolylines = []; this._totalLenChaos = 1; return; }
    // Rebuild only if topology changed.
    if(this._chaosPolylines && this._chaosPolylines.length === src.length){
      let ok = true;
      for(let i=0;i<src.length;i++){
        if(!this._chaosPolylines[i] || this._chaosPolylines[i].length !== src[i].length){ ok=false; break; }
      }
      if(ok) return;
    }
    this._chaosPolylines = src.map(line => line.map(() => ({x:0,y:0,s:0})));
    this._totalLenChaos = 1;
  }


  _updateSignal(dt){
    if(!this.signalEnabled){
      this._sig=1.0; this._sigTarget=1.0;
      this._wasLost=false; this._cycledThisRelock=false;
      this._morphPending=false; this._morphThisCycle=false;
      this._morphHide=false; this._morphSwapped=false; this._morphChaos=0;
      this._relockActive=false; this._relockReveal=1;
      return;
    }

    this._sigTimer-=dt;
    if(this._sigTimer<=0){
      if(this._sigTarget>=0.9){
        // start drop (lock -> lost)
        // Drop much deeper so the silhouette fully dissolves into static.
        this._sigTarget=0.02+Math.random()*0.10;
        this._sigTimer=this._rand(this.signalDropMin,this.signalDropMax);
        this._wasLost=true;
        this._cycledThisRelock=false;

        // schedule cycling during the *deepest* static, so the next SVG "forms out of noise"
        this._morphPending = !!this.cycleOnRelock;
        this._morphThisCycle = false;
        this._morphHide = false;
        this._morphSwapped = false;



      } else {
        // start relock (lost -> lock)
        this._sigTarget=1.0;
        this._sigTimer=this._rand(this.signalHoldMin,this.signalHoldMax);

        if(this._morphThisCycle){
          // reveal gate starts at 0 and grows while signal comes back
          this._morphHide = false;
          this._relockActive=true;
          this._relockT=0;
          this._relockReveal=0;
        }
      }
    }

    const rate=(this._sigTarget>this._sig)?11.0:7.0;
    this._sig += (this._sigTarget-this._sig)*(1-Math.exp(-dt*rate));
    this._sig=clamp(this._sig,0,1);

    // If cycling is enabled, swap SVG during the deepest noise, not on relock.
    // This makes it look like: image -> noise -> (new image emerges from noise).
    // Swap only when we're near the *deepest* part of the drop, so it reads as: shape -> chaos -> new shape.
    const deep = Math.max(0.02, Math.min(this.morphDeepSig, (this._sigTarget||0) + 0.04));
    if(this.cycleOnRelock && this._wasLost && !this._cycledThisRelock && this._morphPending && this._sigTarget<0.9 && this._sig < deep){
      this._cycledThisRelock=true;
      this._morphPending=false;
      this._morphThisCycle=true;
      this._morphHide = true; // hide new SVG during deep static
      this._morphSwapped = true;
      this.nextSvg();
    }

    if(this._sigTarget>0.9 && this._sig>0.95) {
      this._wasLost=false;
      this._morphPending=false;
      if(!this._relockActive) this._morphThisCycle=false;
      this._morphHide = false;
    }
  }

  _mapToCanvas(p){
    return {
      x: (p.x - this._vb.x)*this._scale + this._dx,
      y: (p.y - this._vb.y)*this._scale + this._dy
    };
  }

  _updateGlitch(dt, sig){
    // Glitch should distort the *input geometry* (pre-oscillator), not post-process the final image.
    // We model glitch as a few horizontal "bands" that shift parts of the silhouette for a short time.
    if(!this.glitch){
      this._gActive=0; this._gT=999; this._gDur=0; this._gBands.length=0;
      return;
    }

    // decay
    this._gT += dt;
    if(this._gT >= this._gDur){
      this._gActive = 0;
    } else {
      // ease-out
      const u = 1 - (this._gT / Math.max(1e-6, this._gDur));
      this._gActive = u*u;
    }

    // trigger a new glitch burst when inactive
    if(this._gActive <= 0){
      const rate = this.glitchChance * (1 + (1-sig)) * 2.2; // bursts/sec-ish
      if(Math.random() < rate * dt){
        this._gT = 0;
        this._gDur = 0.06 + Math.random()*0.18; // short, punchy
        const h = this._h || 1;
        const px = this._px || 1;
        const ab = (Number.isFinite(this.rgbAberration) ? this.rgbAberration : 0);

        const bands = [];
        const n = 2 + ((Math.random()*5)|0);
        for(let i=0;i<n;i++){
          const sh = (h*(0.05 + Math.random()*0.18))|0;
          const sy = (Math.random()*(h-sh))|0;

          // dx is the main distortion; scale by aberr slider a bit so one knob boosts both.
          const dx = ((Math.random()*2-1) * (10 + ab*1.25) * px);
          const dy = ((Math.random()*2-1) * (2 + ab*0.25) * px);

          bands.push({ y0: sy, y1: sy+sh, dx, dy });
        }
        this._gBands = bands;
      }
    }
  }

  _glitchOffset(y){
    // Writes result into this._gdx / this._gdy to avoid allocations in hot loops.
    let dx=0, dy=0;
    const a = this._gActive;
    if(a>0 && this._gBands && this._gBands.length){
      const bands=this._gBands;
      for(let i=0;i<bands.length;i++){
        const b=bands[i];
        if(y<b.y0 || y>b.y1) continue;
        const len = Math.max(1, (b.y1-b.y0));
        const t = (y - b.y0)/len; // 0..1
        // soften edges so it looks analog, not blocky
        const edge=0.18;
        let w = 1;
        if(t<edge) w = t/edge;
        else if(t>1-edge) w = (1-t)/edge;
        dx += b.dx * w;
        dy += b.dy * w;
      }
      dx *= a;
      dy *= a;
    }
    this._gdx = dx;
    this._gdy = dy;
  }

  _frame(dt){
    this._t += dt;
    this._updateSignal(dt);

    const ctx=this.ctx;
    const w=this._w, h=this._h;
    const sig=this._sig;

    // Organic morph reveal (only active after a cycle during signal loss)
    if(this._relockActive){
      this._relockT += dt;
      const u = (this.morphRevealDur>0) ? (this._relockT/this.morphRevealDur) : 1;
      const tt = clamp(u,0,1);
      this._relockReveal = tt*tt*(3-2*tt);
      if(this._relockReveal>=1){ this._relockActive=false; this._relockReveal=1; }
    } else {
      this._relockReveal = 1;
    }


    const reveal=this._relockReveal||1;
    // Rays-only morph: blend silhouette <-> chaotic trace depending on signal lock.
    const mix = this._computeShapeMix(sig, reveal);
    this._shapeMix = mix;
    this._morphChaos = mix;
    if(mix > 0.001) this._updateChaosPolylinesDynamic(mix);
    this._updateGlitch(dt, sig);
    if(this.transparent){
      // Fade existing pixels without adding black (keeps transparency)
      ctx.globalCompositeOperation='destination-out';
      ctx.fillStyle='rgba(0,0,0,'+this.trailFade+')';
      ctx.fillRect(0,0,w,h);
      ctx.globalCompositeOperation='source-over';
    } else {
      ctx.globalCompositeOperation='source-over';
      ctx.fillStyle=rgba(this._bgRgb,this.trailFade);
      ctx.fillRect(0,0,w,h);
    }

    const jFrame=this.frameJitter*this._px;
    const jx=jFrame*0.45*this._noise(this._t*5.1, 1.1);
    const jy=jFrame*0.45*this._noise(this._t*5.7, 9.7);
    ctx.save();
    ctx.translate(jx, jy);

    // Extra analog phase wobble during loss (applies to the trace itself, no overlays).
    if(mix>0.05){
      const k = mix*mix;
      const ang = this._noise(this._t*0.32, 7.2) * 0.035 * k;
      const sx = 1 + this._noise(this._t*0.22, 11.4) * 0.025 * k;
      const sy = 1 + this._noise(17.1, this._t*0.24) * 0.025 * k;
      ctx.translate(w*0.5, h*0.5);
      ctx.rotate(ang);
      ctx.scale(sx, sy);
      ctx.translate(-w*0.5, -h*0.5);
    }

    this._drawOutlineBase(sig);
    this._drawBeams(sig);

    ctx.restore();

    // No extra overlays/post; chaos is produced only by the rays.

    if(this.scanlinesEnabled) this._drawScanlines();
    if(this.vignetteSize>0) this._drawVignette();
  }

  _drawOutlineBase(sig){
    const ctx=this.ctx;
    const reveal=this._relockReveal||1;
    const mix = this._shapeMix || 0; // 0=silhouette, 1=chaos

    let jitter=lerp(this.stableJitterPx, this.lostJitterPx, (1-sig)*this.signalNoise);
    let drop=clamp(this.dropout + (1-sig)*0.55*this.signalNoise, 0, 0.92);
    if(reveal<1){
      jitter *= (1 + (1-reveal)*0.9);
      drop = clamp(drop + (1-reveal)*0.28, 0, 0.96);
    }
    if(mix>0){
      const c2 = mix*mix;
      jitter *= (1 + c2*5.8);
      drop = clamp(drop + c2*0.62, 0, 0.985);
    }

    ctx.save();
    ctx.globalCompositeOperation='lighter';
    ctx.strokeStyle=this.baseOutlineColor;
    ctx.lineWidth=Math.max(0.6, (this.baseOutlineStrokePx||this.strokePx)*this._px);
    ctx.lineJoin='miter';
    ctx.miterLimit=this.miterLimit;
    ctx.lineCap='square';

    const glow= (this.glowPx*this._px) * (0.85+0.6*this.intensity);
    ctx.shadowColor=this.baseOutlineColor;
    ctx.shadowBlur=glow;

    // Keep visibility in chaos; we morph geometry instead of fading it out.
    ctx.globalAlpha=clamp(this.baseOutlineAlpha*(0.30+0.70*sig)*this.intensity*(0.75+0.55*mix), 0, 1);

    ctx.beginPath();

    const t=this._t;
    const vb=this._vb;
    const sc=this._scale;
    const dx0=this._dx, dy0=this._dy;
    const src=this._polylines || [];
    const cha=(mix>0.001) ? (this._chaosPolylines||null) : null;

    for(let li=0; li<src.length; li++){
      const line = src[li];
      const cLine = cha ? cha[li] : null;
      let first=true;
      for(let i=0;i<line.length;i++) {
        const p0=line[i];
        if(!p0) continue;

        // Reveal gating only makes sense while the silhouette is actually present.
        if(reveal<0.999 && mix<0.985){
          const nr = 0.5 + 0.5*this._noise(p0.x*0.28 + 17.13, p0.y*0.28 + t*0.55);
          const soft = 0.18;
          const gate = clamp((reveal - nr)/soft, 0, 1);
          if(gate<=0){ first=true; continue; }
          const d = 0.5 + 0.5*this._noise(p0.x*1.15 + 101.3, p0.y*1.15 + 7.9);
          if(d>gate){ first=true; continue; }
        }

        if(drop>0.001 && Math.random()<drop) { first=true; continue; }

        // Blend between silhouette and chaos in VIEWBOX space.
        let xV = p0.x, yV = p0.y;
        if(mix>0.001 && cLine && cLine[i]){
          const pc = cLine[i];
          xV = lerp(xV, pc.x, mix);
          yV = lerp(yV, pc.y, mix);
        }

        // Map to canvas without allocations.
        const pnX = (xV - vb.x)*sc + dx0;
        const pnY = (yV - vb.y)*sc + dy0;

        const n1=this._noise(xV*0.7 + t*7.2, yV*0.7 + 3.1);
        const n2=this._noise(xV*0.7 + 5.7, yV*0.7 + t*7.8);
        const ox=n1*jitter*this._px;
        const oy=n2*jitter*this._px;

        if(this._gActive>0 && mix<0.92){ this._glitchOffset(pnY); } else { this._gdx=0; this._gdy=0; }

        const x=pnX+ox+this._gdx, y=pnY+oy+this._gdy;
        if(first){ctx.moveTo(x,y); first=false;}
        else ctx.lineTo(x,y);
      }
    }

    ctx.stroke();
    ctx.restore();
  }

  _drawBeams(sig){
    const ctx=this.ctx;
    const mix = this._shapeMix || 0;
    const reveal=this._relockReveal||1;

    const L0=this._totalLenView || 1;
    const L1=this._totalLenChaos || L0;
    const L=lerp(L0, L1, mix);

    const dir=this.direction;
    const travel = L * (0.20 + 0.40*this.intensity) * (this.speed*this.speedBoost) * dir;

    const scale=this._scale;
    const widthView = (this.highlightWidthPx*this._px) / scale;
    const k = 3.0;

    const beamCount=this.beams|0;
    const phases=(beamCount===1)?[0]:(beamCount===2)?[0,0.42]:[0,0.42,0.76];

    const bins=6;
    const paths=new Array(bins).fill(0).map(()=>[]);

    const t=this._t;
    let jitter=lerp(this.stableJitterPx*0.7, this.lostJitterPx*0.9, (1-sig)*this.signalNoise);
    if(reveal<1){ jitter *= (1 + (1-reveal)*0.9); }
    if(mix>0){ const c2=mix*mix; jitter *= (1 + c2*5.2); }

    const vb=this._vb;
    const sc=this._scale;
    const dx0=this._dx, dy0=this._dy;
    const src=this._polylines || [];
    const cha=(mix>0.001) ? (this._chaosPolylines||null) : null;

    for(let li=0; li<src.length; li++){
      const line = src[li];
      const cLine = cha ? cha[li] : null;
      for(let i=0;i<line.length-1;i++) {
        const a0=line[i], b0=line[i+1];
        if(!a0||!b0) continue;

        // Blend s/x/y in viewBox space (no extra noise layers).
        let axV=a0.x, ayV=a0.y, aS=a0.s;
        let bxV=b0.x, byV=b0.y;
        if(mix>0.001 && cLine){
          const ca=cLine[i], cb=cLine[i+1];
          if(ca){ axV=lerp(axV, ca.x, mix); ayV=lerp(ayV, ca.y, mix); aS=lerp(aS, ca.s, mix); }
          if(cb){ bxV=lerp(bxV, cb.x, mix); byV=lerp(byV, cb.y, mix); }
        }

        let bright=0;
        for(let bi=0;bi<beamCount;bi++) {
          const pos = mod((t*travel) + phases[bi]*L, L);
          const d0 = Math.abs(aS - pos);
          const d = Math.min(d0, L-d0);
          const x = d/widthView;
          const w = Math.exp(-k*x*x);
          bright = Math.max(bright, w);
        }
        bright *= (0.35+0.65*sig);

        // Reveal gating only while silhouette is visible.
        if(reveal<0.999 && mix<0.985){
          const nr = 0.5 + 0.5*this._noise(a0.x*0.28 + 21.7, a0.y*0.28 + t*0.55);
          const soft = 0.18;
          const gate = clamp((reveal - nr)/soft, 0, 1);
          bright *= gate;
        }

        if(bright < 0.08) continue;

        const paX = (axV - vb.x)*sc + dx0;
        const paY = (ayV - vb.y)*sc + dy0;
        const pbX = (bxV - vb.x)*sc + dx0;
        const pbY = (byV - vb.y)*sc + dy0;

        const n1=this._noise(axV*0.8 + t*8.5, ayV*0.8 + 1.7);
        const n2=this._noise(axV*0.8 + 4.9, ayV*0.8 + t*8.9);
        const ox=n1*jitter*this._px;
        const oy=n2*jitter*this._px;

        if(this._gActive>0 && mix<0.92){ this._glitchOffset((paY+pbY)*0.5); } else { this._gdx=0; this._gdy=0; }

        const x1=paX+ox+this._gdx, y1=paY+oy+this._gdy;
        const x2=pbX+ox+this._gdx, y2=pbY+oy+this._gdy;

        const bin = Math.min(bins-1, Math.max(0, Math.floor(bright*(bins-1)+0.0001)));
        paths[bin].push([x1,y1,x2,y2]);
      }
    }

    ctx.save();
    ctx.globalCompositeOperation='lighter';
    ctx.lineJoin='miter';
    ctx.miterLimit=this.miterLimit;
    ctx.lineCap='square';
    ctx.strokeStyle=this.beamColor;

    for(let bi=0;bi<bins;bi++) {
      const segs=paths[bi];
      if(!segs.length) continue;
      const a = (bi/(bins-1));
      const alpha = clamp((0.06 + 0.42*a)*this.intensity, 0, 0.95);
      const lw = (this.strokePx*(1.1+1.6*a))*this._px;
      const blur = (this.glowPx*(1.0+1.3*a))*this._px*(0.85+0.7*this.intensity);

      ctx.globalAlpha=alpha;
      ctx.lineWidth=lw;
      ctx.shadowColor=this.beamColor;
      ctx.shadowBlur=blur;

      ctx.beginPath();
      for(const s of segs) {
        ctx.moveTo(s[0],s[1]);
        ctx.lineTo(s[2],s[3]);
      }
      ctx.stroke();
    
      // --- RGB aberration (visible) ---
      // Instead of pixel-level channel splitting (expensive), we draw a couple of offset colored passes
      // during active glitch frames. This creates a clear CRT/RGB-fringe effect on bright strokes.
      if(this._gActive>0 && this.rgbAberration>0 && bi >= bins-2){
        const ab = (this.rgbAberration*this._px) * (0.55 + 0.55*Math.random()) * this._gActive;
        const a2 = alpha * (0.35 + 0.35*this._gActive);

        // Green-cyan fringe
        ctx.globalAlpha = clamp(a2, 0, 0.85);
        ctx.lineWidth = lw*0.92;
        ctx.shadowBlur = blur*0.25;
        ctx.strokeStyle = 'rgba(0,255,170,0.95)';
        ctx.shadowColor = 'rgba(0,255,170,0.85)';
        ctx.beginPath();
        for(const s of segs){
          ctx.moveTo(s[0]+ab, s[1]);
          ctx.lineTo(s[2]+ab, s[3]);
        }
        ctx.stroke();

        // Blue fringe
        ctx.strokeStyle = 'rgba(90,150,255,0.95)';
        ctx.shadowColor = 'rgba(90,150,255,0.85)';
        ctx.beginPath();
        for(const s of segs){
          ctx.moveTo(s[0]-ab, s[1]);
          ctx.lineTo(s[2]-ab, s[3]);
        }
        ctx.stroke();

        // restore beam strokeStyle (next bins)
        ctx.strokeStyle = this.beamColor;
        ctx.shadowColor = this.beamColor;
      }
}

    ctx.restore();
  }

  _drawNoiseBursts(amount){
    const ctx=this.ctx;
    const w=this._w, h=this._h;

    const n = Math.floor(10 + 70*amount*this.signalNoise);
    ctx.save();
    ctx.globalCompositeOperation='lighter';
    ctx.strokeStyle=this.beamColor;
    ctx.lineWidth=Math.max(0.6, this.strokePx*this._px*0.9);
    ctx.globalAlpha=clamp(0.06 + 0.18*amount, 0, 0.35);
    ctx.shadowColor=this.beamColor;
    ctx.shadowBlur=(this.glowPx*1.1)*this._px;

    for(let i=0;i<n;i++) {
      const x=Math.random()*w;
      const y=Math.random()*h;
      const len=(6+Math.random()*28)*this._px*(1+amount);
      const ang=Math.random()*Math.PI*2;
      ctx.beginPath();
      ctx.moveTo(x,y);
      ctx.lineTo(x+Math.cos(ang)*len, y+Math.sin(ang)*len);
      ctx.stroke();
    }
    ctx.restore();
  }

  _rgbAberrateStrip(sy, sh, strengthPx){
    // Fast-ish RGB split without per-pixel loops: we tint three shifted copies using an offscreen strip.
    const ctx=this.ctx;
    const w=this._w;
    const s=Math.max(0, strengthPx|0);
    if(s < 1 || sh < 2) return;

    const oc = this._abStrip || (this._abStrip = document.createElement('canvas'));
    if(oc.width !== w || oc.height !== sh){
      oc.width = w; oc.height = sh;
      this._abCtx = oc.getContext('2d', { willReadFrequently:false });
    }
    const tctx = this._abCtx;

    // offsets for channels
    const rdx = (( Math.random()*2-1) * s)|0;
    const gdx = (( Math.random()*2-1) * s)|0;
    const bdx = (( Math.random()*2-1) * s)|0;

    // draw strip snapshot into temp
    tctx.setTransform(1,0,0,1,0,0);
    tctx.globalCompositeOperation='source-over';
    tctx.globalAlpha=1;
    tctx.clearRect(0,0,w,sh);
    tctx.drawImage(this.canvas, 0, sy, w, sh, 0, 0, w, sh);

    // helper to tint and draw
    const drawTint = (color, dx, alpha) => {
      tctx.globalCompositeOperation='source-in';
      tctx.fillStyle=color;
      tctx.fillRect(0,0,w,sh);
      ctx.save();
      ctx.globalCompositeOperation='lighter';
      ctx.globalAlpha=alpha;
      ctx.drawImage(oc, dx, sy);
      ctx.restore();

      // restore snapshot for next channel
      tctx.globalCompositeOperation='source-over';
      tctx.globalAlpha=1;
      tctx.clearRect(0,0,w,sh);
      tctx.drawImage(this.canvas, 0, sy, w, sh, 0, 0, w, sh);
    };

    const a = clamp(0.42 + s*0.015, 0.35, 0.85);
    drawTint('rgb(255,40,40)', rdx, a);
    drawTint('rgb(40,255,120)', gdx, a*0.85);
    drawTint('rgb(60,140,255)', bdx, a*0.75);
  }

  _glitchStrips(){
    const ctx=this.ctx;
    const w=this._w,h=this._h;
    const strips=2+((Math.random()*3)|0);
    for(let i=0;i<strips;i++){
      const sh=Math.max(2,(h*(0.02+Math.random()*0.05))|0);
      const sy=(Math.random()*(h-sh))|0;
      const ab = (Number.isFinite(this.rgbAberration) ? this.rgbAberration : 0);
      const dx=((Math.random()*2-1)*(16+ab*1.8)*this._px)|0;
      ctx.globalCompositeOperation='source-over';
      ctx.drawImage(this.canvas,0,sy,w,sh,dx,sy,w,sh);
      ctx.globalCompositeOperation='lighter';
      if(this.rgbAberration>0 && Math.random()<0.9){
        this._rgbAberrateStrip(sy, sh, Math.max(1, (this.rgbAberration*this._px)));
      }
    }
  }

  _updateVignetteTexture(){
    // Vignette is implemented as an ALPHA mask (edges fade to transparent).
    // this.vignetteSize: 0..1 (0 = off, 1 = largest vignette / smallest clear center)
    const t = clamp(this.vignetteSize || 0, 0, 1);
    if(t<=0){ this._vgKey=''; return; }

    const w=this._w||0, h=this._h||0;
    if(w<2 || h<2) return;

    const key = w+'x'+h+'@'+t.toFixed(3);
    if(this._vgKey===key) return;
    this._vgKey=key;

    const c = this._vignetteFull || (this._vignetteFull=document.createElement('canvas'));
    if(c.width!==w || c.height!==h){ c.width=w; c.height=h; }
    const vctx = this._vignetteCtx || (this._vignetteCtx=c.getContext('2d'));

    vctx.setTransform(1,0,0,1,0,0);
    vctx.globalCompositeOperation='source-over';
    vctx.globalAlpha=1;
    vctx.clearRect(0,0,w,h);

    // Use half-diagonal so corners fade evenly.
    const r = Math.sqrt(w*w + h*h) * 0.5;

    // 'Size' controls how far inward the fade starts.
    // t=0.01 => very thin edge fade; t=1 => strong fade reaching close to the center.
    const inner = Math.max(r*0.15, r * (1 - 0.85*t));
    const outer = r * 1.02;

    const g = vctx.createRadialGradient(w/2,h/2, inner, w/2,h/2, outer);
    // Inside 'inner' radius alpha is 1, towards 'outer' it goes to 0.
    g.addColorStop(0.00,'rgba(255,255,255,1)');
    g.addColorStop(0.70,'rgba(255,255,255,0.35)');
    g.addColorStop(1.00,'rgba(255,255,255,0)');

    vctx.fillStyle=g;
    vctx.fillRect(0,0,w,h);
  }

  _drawScanlines(){
    const ctx=this.ctx;
    const w=this._w,h=this._h;
    ctx.save();
    ctx.globalCompositeOperation='source-over';
    ctx.globalAlpha=0.14;
    const pattern=ctx.createPattern(this._scanTile,'repeat');
    ctx.fillStyle=pattern;
    ctx.fillRect(0,0,w,h);

    ctx.globalAlpha=0.04;
    ctx.fillStyle=rgba(this._rgb,1);
    const y=((this._t*55*this._px)|0)%h;
    ctx.fillRect(0,y,w,Math.max(1,(1*this._px)|0));
    ctx.restore();
  }

  _drawVignette(){
    if(!this.vignetteSize || this.vignetteSize<=0) return;
    this._updateVignetteTexture();

    const ctx=this.ctx;
    const w=this._w,h=this._h;
    if(!this._vignetteFull || w<2 || h<2) return;

    ctx.save();
    // Apply the vignette as an alpha mask: fade edges to transparent.
    ctx.globalCompositeOperation='destination-in';
    ctx.globalAlpha=1;
    ctx.drawImage(this._vignetteFull,0,0);
    ctx.restore();
  }
}

window.OsciLogoAnimator = OsciLogoAnimator;

})();
